/**
 * How many people — the second half of what used to be Donate a Meal.
 *
 * On one screen the headcount sat under every meal card, so on a small phone a
 * donor chose a meal and then had to scroll past the rest of the menu to find
 * the question that decides the price. Split, each screen asks one thing and
 * its answer is always within reach.
 *
 * The pinned total is the *server* quote: the tiles preview a price for
 * responsiveness, but the figure carried into checkout is the one issued.
 */

import { useEffect } from 'react';
import { StyleSheet, View, useWindowDimensions } from 'react-native';

import { formatMoney, formatPeople, validatePeopleCount } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { ArrowIcon } from '../../components/common/Icon';
import { ErrorState } from '../../components/feedback/States';
import { QuantitySelector } from '../../components/donation/QuantitySelector';
import { QuantityStepper } from '../../components/donation/QuantityStepper';
import { HeaderPill } from '../../components/layout/HeaderPill';
import { Glyph } from '../../components/common/Icon';
import { sizing } from '../../theme';
import { useQuantityLayout } from './useQuantityLayout';
import { SelectedMealCard } from '../../components/donation/SelectedMealCard';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import { Skeleton } from '../../components/feedback/Skeleton';
import { Text } from '../../components/common/Text';
import { useAppConfig, useQuote } from '../../core/api/hooks';
import { useDonationStore } from './donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

/**
 * Below this height the meal is a line under the title, so every headcount
 * tile and Continue to Checkout still fit a small phone without scrolling.
 * Above it there is room to show the meal itself, and the screen was
 * otherwise mostly empty.
 */
const ROOMY_VIEWPORT = 740;

export function PeopleCountScreen({ navigation }: DonorScreenProps<'PeopleCount'>) {
  const t = useT();
  const pick = useLocalized();
  const appConfig = useAppConfig();
  const { height } = useWindowDimensions();
  const roomy = height >= ROOMY_VIEWPORT;
  const [layout, toggleLayout] = useQuantityLayout();

  const meal = useDonationStore((s) => s.meal);
  const peopleCount = useDonationStore((s) => s.peopleCount);
  const setPeopleCount = useDonationStore((s) => s.setPeopleCount);
  const setQuote = useDonationStore((s) => s.setQuote);

  const quote = useQuote(meal?.id ?? null, peopleCount);

  // Mirror the fetched quote into the draft so later screens read one source.
  // In an effect, so the store is never written while React is rendering.
  useEffect(() => {
    setQuote(quote.data ?? null);
  }, [quote.data, setQuote]);

  // Reachable only through the meal screen; if the draft was lost (a reload
  // on web), go back to where the choice is made rather than price nothing.
  useEffect(() => {
    if (!meal) navigation.replace('DonateMeal');
  }, [meal, navigation]);

  const countError =
    peopleCount !== null && appConfig.data
      ? validatePeopleCount(peopleCount, appConfig.data)
      : null;

  const canContinue = Boolean(meal && peopleCount && !countError && quote.data);

  if (appConfig.isError) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-people-count">
        <ErrorState onRetry={() => void appConfig.refetch()} />
      </ScreenShell>
    );
  }

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      testID="screen-people-count"
      /* Same place and same pill as the meal screen's layout switch. Shows
         the design a tap switches *to*. */
      trailing={
        <HeaderPill
          onPress={toggleLayout}
          accessibilityLabel={
            layout === 'tiles' ? t('Switch to counter') : t('Switch to tiles')
          }
          selected={layout === 'stepper'}
          testID="quantity-layout-toggle"
        >
          <Glyph name={layout === 'tiles' ? 'counter' : 'grid'} size={sizing.icon.md} />
        </HeaderPill>
      }
      subHeader={
        <ScreenTitle
          title={t('How many people would you like to feed?')}
          subtitle={
            meal && !roomy
              ? `${pick(meal.name, meal.nameUr)} · ${formatMoney(meal.unitPrice)} ${t('per person')}`
              : undefined
          }
        />
      }
      pinnedFooter={
        <View style={styles.pinned}>
          <View style={styles.summaryRow}>
            <View style={styles.summaryText}>
              <Text variant="caption" tone="tertiary">{t('You selected')}</Text>
              <Text variant="cardTitle" numberOfLines={1}>
                {peopleCount ? formatPeople(peopleCount) : t('Choose how many people')}
              </Text>
            </View>

            <View style={styles.totalBlock}>
              <Text variant="caption" tone="tertiary" align="right">{t('Total Amount')}</Text>
              {quote.isFetching ? (
                <Skeleton width={96} height={24} />
              ) : (
                <Text variant="amount" tone="brand" align="right" testID="pinned-total">
                  {quote.data ? formatMoney(quote.data.totalAmount) : '—'}
                </Text>
              )}
            </View>
          </View>

          <Button
            label={t('Continue to Checkout')}
            onPress={() => navigation.navigate('OrderDetails')}
            disabled={!canContinue}
            loading={quote.isFetching && Boolean(peopleCount)}
            trailingIcon={<ArrowIcon />}
            testID="cta-continue-checkout"
          />
        </View>
      }
    >
      {meal && roomy ? (
        <View style={styles.meal}>
          <SelectedMealCard meal={meal} />
        </View>
      ) : null}

      {appConfig.data ? (
        layout === 'tiles' ? (
          <QuantitySelector
            presets={appConfig.data.people.presets}
            unitPrice={meal?.unitPrice ?? null}
            value={peopleCount}
            onChange={setPeopleCount}
            rules={appConfig.data.people}
            error={countError}
          />
        ) : (
          <QuantityStepper
            presets={appConfig.data.people.presets}
            unitPrice={meal?.unitPrice ?? null}
            value={peopleCount}
            onChange={setPeopleCount}
            rules={appConfig.data.people}
            error={countError}
          />
        )
      ) : (
        <Skeleton height={72} />
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  meal: {
    marginBottom: space.lg,
  },
  pinned: {
    gap: space.md,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.md,
  },
  summaryText: {
    flex: 1,
    gap: space.xxs,
  },
  totalBlock: {
    alignItems: 'flex-end',
    gap: space.xxs,
  },
});
